import { useCallback, useEffect, useMemo, useState } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../context/AuthContext';
import { businessDate, businessRangeBounds, moveBusinessAnchor, formatBusinessDate, formatBusinessMonth } from '../lib/businessTime';

const money=n=>new Intl.NumberFormat('en-US',{style:'currency',currency:'USD',maximumFractionDigits:0}).format(Number(n||0));
const pct=(a,b)=>b>0?`${Math.round((a/b)*100)}%`:'—';
const REPORT_MODE_KEY='lead-tracker:reports:mode';
const REPORT_MODES=new Set(['day','week','month','custom']);

function initialReportMode(){
  try{
    const saved=sessionStorage.getItem(REPORT_MODE_KEY);
    return REPORT_MODES.has(saved)?saved:'month';
  }catch{
    return 'month';
  }
}

export default function ReportsPage(){
  const {workspace}=useAuth();
  const today=businessDate();
  const [mode,setMode]=useState(initialReportMode),[anchor,setAnchor]=useState(today),[motion,setMotion]=useState('all');
  const [customStart,setCustomStart]=useState(today),[customEnd,setCustomEnd]=useState(today);
  const [events,setEvents]=useState([]),[loading,setLoading]=useState(true),[error,setError]=useState('');
  const range=useMemo(()=>businessRangeBounds(mode,anchor,customStart,customEnd),[mode,anchor,customStart,customEnd]);

  useEffect(()=>{
    try{sessionStorage.setItem(REPORT_MODE_KEY,mode)}catch{}
  },[mode]);

  const load=useCallback(async()=>{
    setLoading(true);setError('');
    const {data,error:e}=await supabase.from('activity_events')
      .select('id,event_type,motion,numeric_value,occurred_at,lead_id,leads!activity_events_workspace_id_lead_id_fkey(id,validity)')
      .eq('workspace_id',workspace.id).gte('occurred_at',range.start.toISOString()).lt('occurred_at',range.end.toISOString());
    if(e)setError(e.message);
    else setEvents((data||[]).filter(x=>!x.lead_id||x.leads?.validity==='valid'));
    setLoading(false);
  },[workspace.id,range.start.getTime(),range.end.getTime()]);
  useEffect(()=>{load()},[load]);

  const filtered=useMemo(()=>events.filter(e=>motion==='all'||e.motion===motion),[events,motion]);
  const count=type=>filtered.filter(e=>e.event_type===type).reduce((s,e)=>s+Number(e.numeric_value||0),0);
  const outboundAcquisitionCount=type=>events.filter(e=>(motion==='inbound'?false:e.motion==='outbound')&&e.event_type===type).reduce((s,e)=>s+Number(e.numeric_value||0),0);

  const m=useMemo(()=>({
    dms:outboundAcquisitionCount('prospect_contacted'),
    responses:outboundAcquisitionCount('response'),
    conversations:outboundAcquisitionCount('conversation_started'),
    leads:count('lead_identified'), offered:count('offered'), forms:count('form_submitted'), qualified:count('qualified'),
    booked:count('call_booked'), showed:count('showed'), noShow:count('no_show'), won:count('closed_won'),
    lost:count('closed_lost'), revenue:count('payment_received'),
  }),[filtered,events,motion]);

  const startDate=formatBusinessDate(range.start);
  const periodLabel=mode==='day'?startDate:mode==='week'?`${startDate} – ${formatBusinessDate(new Date(range.end.getTime()-1))}`:mode==='month'?formatBusinessMonth(range.start):`${startDate} – ${formatBusinessDate(new Date(range.end.getTime()-1))}`;
  const funnel = motion==='inbound'
    ? [['Leads',m.leads],['Offered',m.offered],['Forms Submitted',m.forms],['Qualified',m.qualified],['Booked',m.booked],['Showed',m.showed],['Closed Won',m.won]]
    : [['DMs Sent',m.dms],['Responses',m.responses],['Conversations',m.conversations],['Leads',m.leads],['Offered',m.offered],['Forms Submitted',m.forms],['Qualified',m.qualified],['Booked',m.booked],['Showed',m.showed],['Closed Won',m.won]];

  return <section className="page-stack">
    <div><h1 className="page-title">Reports</h1><p className="page-description">Business outcomes derived from factual CRM events using the New York business day.</p></div>

    <div className="panel activity-controls">
      <div className="segmented">{[['day','Daily'],['week','Weekly'],['month','Monthly'],['custom','Custom']].map(([k,l])=><button key={k} className={mode===k?'active':''} onClick={()=>setMode(k)}>{l}</button>)}</div>
      {mode!=='custom'?<div className="period-nav"><button className="secondary-button inline-button" onClick={()=>setAnchor(moveBusinessAnchor(anchor,mode,-1))}>←</button><strong>{periodLabel}</strong><button className="secondary-button inline-button" onClick={()=>setAnchor(moveBusinessAnchor(anchor,mode,1))}>→</button></div>:
      <div className="custom-range"><input className="text-input" type="date" max={today} value={customStart} onChange={e=>setCustomStart(e.target.value)}/><span>to</span><input className="text-input" type="date" max={today} min={customStart} value={customEnd} onChange={e=>setCustomEnd(e.target.value)}/></div>}
      <div className="segmented motion-filter">{[['all','All'],['outbound','Outbound'],['inbound','Inbound']].map(([k,l])=><button key={k} className={motion===k?'active':''} onClick={()=>setMotion(k)}>{l}</button>)}</div>
    </div>

    {error&&<div className="error-message">{error}</div>}
    {loading?<div className="panel empty-state">Loading reports…</div>:<>
      <div className="report-outcomes">
        <div className="metric-card report-hero"><span>Revenue Collected</span><strong>{money(m.revenue)}</strong><small>{periodLabel}</small></div>
        <div className="metric-card"><span>Qualified Leads</span><strong>{m.qualified}</strong><small>{pct(m.qualified,m.forms)} of submitted forms</small></div>
        <div className="metric-card"><span>Calls Booked</span><strong>{m.booked}</strong><small>{pct(m.booked,m.qualified)} of qualified</small></div>
        <div className="metric-card"><span>Show Rate</span><strong>{pct(m.showed,m.showed+m.noShow)}</strong><small>{m.showed} showed · {m.noShow} no-show</small></div>
        <div className="metric-card"><span>Close Rate</span><strong>{pct(m.won,m.showed)}</strong><small>{m.won} won · {m.lost} lost</small></div>
      </div>

      <div className="panel">
        <div className="panel-head"><div><h2>{motion==='inbound'?'Inbound Funnel':motion==='outbound'?'Outbound Funnel':'Combined Funnel'}</h2><span className="small-note">{periodLabel}</span></div></div>
        <div className="report-funnel">{funnel.map(([label,value],i)=><div className="funnel-step" key={label}><span>{label}</span><strong>{value}</strong>{i>0&&<small>{pct(value,funnel[i-1][1])} from prior</small>}</div>)}</div>
      </div>

      <div className="two-col">
        <div className="panel"><div className="panel-head"><h2>Acquisition</h2></div><div className="detail-lines">
          {motion!=='inbound'&&<><div><span>Outbound DMs Sent</span><strong>{m.dms}</strong></div><div><span>Response Rate</span><strong>{pct(m.responses,m.dms)}</strong></div><div><span>Conversation Rate</span><strong>{pct(m.conversations,m.responses)}</strong></div></>}
          <div><span>Leads Identified</span><strong>{m.leads}</strong></div><div><span>Lead → Offered</span><strong>{pct(m.offered,m.leads)}</strong></div>
        </div></div>
        <div className="panel"><div className="panel-head"><h2>Sales Outcomes</h2></div><div className="detail-lines">
          <div><span>Forms Submitted</span><strong>{m.forms}</strong></div><div><span>Qualified</span><strong>{m.qualified}</strong></div><div><span>Booked</span><strong>{m.booked}</strong></div><div><span>Showed</span><strong>{m.showed}</strong></div><div><span>Closed Won</span><strong>{m.won}</strong></div><div><span>Closed Lost</span><strong>{m.lost}</strong></div><div><span>Revenue Collected</span><strong>{money(m.revenue)}</strong></div>
        </div></div>
      </div>

      <div className="panel report-note"><strong>Counting rule</strong><span>Inbound acquisition begins at Lead Identified. Response and conversation acquisition metrics are outbound-only. Leads later classified Not a Lead are excluded from normal Lead and downstream funnel counts.</span></div>
    </>}
  </section>;
}
